import { useLayoutEffect, useState } from "react";
import { type Theme, useTheme } from "@/hooks/useTheme";

export interface GraphTheme {
	surface: string;
	text: string;
	textMuted: string;
	border: string;
	borderStrong: string;
	accent: string;
	accentSoft: string;
}

const readCssVar = (name: string) => getComputedStyle(document.documentElement).getPropertyValue(name).trim();

const readGraphTheme = (theme: Theme): GraphTheme => ({
	surface: readCssVar("--surface"),
	text: readCssVar("--text"),
	textMuted: readCssVar("--text-muted"),
	border: readCssVar("--border"),
	borderStrong: readCssVar("--border-strong"),
	accent: theme === "dark" ? "#ff6d48" : "#ff441b",
	accentSoft: theme === "dark" ? "#ff8a6b" : "#ff6d48",
});

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const normalizeHexColor = (input: string): string | null => {
	const value = input.trim();
	if (!value.startsWith("#")) return null;

	const hex = value.slice(1);
	if (hex.length === 3) {
		return `#${hex
			.split("")
			.map((char) => `${char}${char}`)
			.join("")}`;
	}

	if (hex.length === 6) {
		return `#${hex}`;
	}

	return null;
};

export const shiftHexColor = (hexColor: string, amount: number): string => {
	const normalizedHex = normalizeHexColor(hexColor);
	if (!normalizedHex) return hexColor;

	const red = Number.parseInt(normalizedHex.slice(1, 3), 16);
	const green = Number.parseInt(normalizedHex.slice(3, 5), 16);
	const blue = Number.parseInt(normalizedHex.slice(5, 7), 16);
	const ratio = clamp(amount, -0.45, 0.45);

	const mix = (channel: number) =>
		Math.round(ratio >= 0 ? channel + (255 - channel) * ratio : channel * (1 + ratio))
			.toString(16)
			.padStart(2, "0");

	return `#${mix(red)}${mix(green)}${mix(blue)}`;
};

interface UseGraphThemeReturn {
	theme: Theme;
	graphTheme: GraphTheme;
}

export const useGraphTheme = (): UseGraphThemeReturn => {
	const [theme] = useTheme();
	const [graphTheme, setGraphTheme] = useState<GraphTheme>(() => readGraphTheme("light"));

	useLayoutEffect(() => {
		setGraphTheme(readGraphTheme(theme));
	}, [theme]);

	return {
		theme,
		graphTheme,
	};
};

export default useGraphTheme;
